import { useEffect, useState } from 'react';
import axios from 'axios';
import WordMultiSearchSelect from '../../components/forms/WordMultiSearchSelect';
import language from '../../variables/language';

const stopProp = (e) => e.stopPropagation();

// Lesson.date is a plain ISO_LOCAL_DATE string, start/end are plain time
// strings (see LessonDetailModal's SHORT_FIELDS) - no parsing needed, just
// concatenation for a readable label.
const formatEventLabel = (event) => {
   const lesson = event.lesson;
   if(!lesson) {
      return '#' + event.id;
   }
   const time = lesson.start && lesson.end ? ` ${lesson.start}-${lesson.end}` : '';
   return (lesson.date || '') + time;
}

export default function QuizGenerationModal({ isOpen, studentId, onClose, onGenerated }) {
   const lang = language[language.lang];
   const t = lang.modals.quizGeneration;

   const [events, setEvents] = useState([]);
   const [lessonId, setLessonId] = useState('');
   const [additionalWords, setAdditionalWords] = useState([]);
   const [difficulty, setDifficulty] = useState('');
   const [questionCount, setQuestionCount] = useState('');
   const [optionsPerQuestion, setOptionsPerQuestion] = useState('');
   const [textAnswerRatio, setTextAnswerRatio] = useState('');
   const [useAiMode, setUseAiMode] = useState('');
   const [autoNaming, setAutoNaming] = useState(true);
   const [title, setTitle] = useState('');
   const [description, setDescription] = useState('');
   const [generating, setGenerating] = useState(false);
   const [error, setError] = useState(null);

   useEffect(() => {
      if(!isOpen) {
         return;
      }
      setLessonId('');
      setAdditionalWords([]);
      setDifficulty('');
      setQuestionCount('');
      setOptionsPerQuestion('');
      setTextAnswerRatio('');
      setUseAiMode('');
      setAutoNaming(true);
      setTitle('');
      setDescription('');
      setError(null);

      axios.get('/api/events', { params: { student: studentId }, withCredentials: true })
         .then(response => setEvents(Array.isArray(response.data) ? response.data : []))
         .catch(() => setEvents([]));
   }, [isOpen, studentId]);

   if(!isOpen) {
      return '';
   }

   const handleGenerate = () => {
      if(generating) {
         return;
      }
      setGenerating(true);
      setError(null);

      const request = { studentId: Number(studentId) };
      if(lessonId) request.lessonId = Number(lessonId);
      if(additionalWords.length > 0) request.additionalWordIds = additionalWords.map(w => w.id);
      if(difficulty) request.difficulty = difficulty;
      if(questionCount) request.questionCount = Number(questionCount);
      if(optionsPerQuestion) request.optionsPerQuestion = Number(optionsPerQuestion);
      // 0 is a meaningful, deliberate override ("force zero text-answer
      // questions") - a truthy check like the fields above would wrongly
      // treat it as "unset" and silently fall back to the config default.
      if(textAnswerRatio !== '') request.textAnswerRatio = Number(textAnswerRatio);
      if(useAiMode !== '') request.useAi = useAiMode === 'true';
      request.autoNaming = autoNaming;
      if(!autoNaming) {
         if(title.trim()) request.title = title.trim();
         if(description.trim()) request.description = description.trim();
      }

      axios.post('/api/quiz-generation/generate', JSON.stringify(request),
         { withCredentials: true, headers: {'Content-Type': 'application/json'} })
         .then(() => {
            onGenerated();
            onClose();
         })
         .catch(err => {
            const message = err.response && typeof err.response.data === 'string' ? err.response.data : t.genericError;
            setError(message);
         })
         .finally(() => setGenerating(false));
   }

   return (
      <div className='modal-menu' onClick={onClose} style={{display:'flex'}}>
         <div className='modal-window quiz-generation-modal-window' onClick={stopProp}>
            <h3 className='modal-w-title'>{t.title}</h3>

            <div className='quiz-generation-modal-body'>
               <label className='quiz-generation-modal-field'>
                  <span>{t.lessonLabel}</span>
                  <select value={lessonId} onChange={(e) => setLessonId(e.target.value)}>
                     <option value=''>{t.lessonDefault}</option>
                     {events.filter(ev => ev.lesson).map(ev => <option key={ev.lesson.id} value={ev.lesson.id}>{formatEventLabel(ev)}</option>)}
                  </select>
               </label>

               <label className='quiz-generation-modal-field'>
                  <span>{t.additionalWordsLabel}</span>
                  <WordMultiSearchSelect value={additionalWords} onChange={setAdditionalWords}
                     placeholder={t.additionalWordsPlaceholder} />
               </label>

               <div className='quiz-generation-modal-row'>
                  <label className='quiz-generation-modal-field'>
                     <span>{t.difficultyLabel}</span>
                     <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                        <option value=''>{t.difficultyDefault}</option>
                        <option value='EASY'>{t.difficultyEasy}</option>
                        <option value='MEDIUM'>{t.difficultyMedium}</option>
                        <option value='HARD'>{t.difficultyHard}</option>
                     </select>
                  </label>
                  <label className='quiz-generation-modal-field'>
                     <span>{t.useAiLabel}</span>
                     <select value={useAiMode} onChange={(e) => setUseAiMode(e.target.value)}>
                        <option value=''>{t.useAiDefault}</option>
                        <option value='true'>{t.useAiOn}</option>
                        <option value='false'>{t.useAiOff}</option>
                     </select>
                  </label>
               </div>

               <div className='quiz-generation-modal-row'>
                  <label className='quiz-generation-modal-field'>
                     <span>{t.questionCountLabel}</span>
                     <input type='number' min='1' placeholder={t.questionCountPlaceholder}
                        value={questionCount} onChange={(e) => setQuestionCount(e.target.value)} />
                  </label>
                  <label className='quiz-generation-modal-field'>
                     <span>{t.optionsPerQuestionLabel}</span>
                     <input type='number' min='2' placeholder={t.optionsPerQuestionPlaceholder}
                        value={optionsPerQuestion} onChange={(e) => setOptionsPerQuestion(e.target.value)} />
                  </label>
               </div>

               <label className='quiz-generation-modal-field'>
                  <span>{t.textAnswerRatioLabel}</span>
                  <input type='number' min='0' max='100' placeholder={t.textAnswerRatioPlaceholder}
                     value={textAnswerRatio} onChange={(e) => setTextAnswerRatio(e.target.value)} />
               </label>

               <label className='quiz-generation-modal-toggle-row'>
                  <input type='checkbox' checked={autoNaming} onChange={(e) => setAutoNaming(e.target.checked)} />
                  <span>{t.autoNamingLabel}</span>
               </label>

               {!autoNaming ? (
                  <>
                     <label className='quiz-generation-modal-field'>
                        <span>{t.titleLabel}</span>
                        <input type='text' placeholder={t.titlePlaceholder}
                           value={title} onChange={(e) => setTitle(e.target.value)} />
                     </label>
                     <label className='quiz-generation-modal-field'>
                        <span>{t.descriptionLabel}</span>
                        <input type='text' placeholder={t.descriptionPlaceholder}
                           value={description} onChange={(e) => setDescription(e.target.value)} />
                     </label>
                  </>
               ) : ''}

               {error ? <p className='quiz-generation-modal-error'>{error}</p> : ''}

               <div className='student-card-form-footer'>
                  <div className='student-card-discard-link' onClick={onClose}>{lang.cancel}</div>
                  <button type='button' className='student-card-save-btn' disabled={generating} onClick={handleGenerate}>
                     {generating ? t.generating : t.generateBtn}
                  </button>
               </div>
            </div>
         </div>
      </div>
   );
}
